import React, { useState, useRef } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { getInitials } from "../../common/utils";

interface AccountDropdownProps {
  userFirstName?: string | null;
  userLastName?: string | null;
  userProfileImage?: string | null;
  settingsClick: () => void;
  logoutClick: () => void;
}

function AccountDropdown(props: AccountDropdownProps) {
  const {
    userFirstName,
    userLastName,
    userProfileImage,
    settingsClick,
    logoutClick,
  } = props;

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuClick = (callback: () => void) => {
    setOpen(false);
    callback();
  };

  const fullName = `${userFirstName || ""} ${userLastName || ""}`.trim();
  const initials = getInitials(fullName) || "U";

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconButton
        ref={anchorRef}
        onClick={handleToggle}
        aria-controls={open ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          p: 0.5,
          borderRadius: 2,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <Avatar
          src={userProfileImage || undefined}
          sx={{
            width: 36,
            height: 36,
            fontSize: 14,
            fontWeight: 600,
            bgcolor: "primary.main",
          }}
        >
          {!userProfileImage && initials}
        </Avatar>
        <Box
          sx={{
            ml: 1,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontWeight: 500,
              maxWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {userFirstName || "User"}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              color: "text.secondary",
              fontSize: 20,
              ml: 0.5,
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        disablePortal
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: "right top",
            }}
          >
            <Paper
              elevation={3}
              sx={{
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* User Info Header */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {fullName || "User"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Manage your account
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Menu Items */}
                  <MenuList sx={{ py: 0.5 }}>
                    <MenuItem
                      onClick={() => handleMenuClick(settingsClick)}
                      sx={{
                        py: 1,
                        px: 2,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="body2">Settings</Typography>
                    </MenuItem>

                    <Divider sx={{ my: 0.5 }} />

                    <MenuItem
                      onClick={() => handleMenuClick(logoutClick)}
                      sx={{
                        py: 1,
                        px: 2,
                        color: "error.main",
                        "&:hover": {
                          backgroundColor: "error.lighter",
                        },
                      }}
                    >
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" sx={{ color: "error.main" }} />
                      </ListItemIcon>
                      <Typography variant="body2">Logout</Typography>
                    </MenuItem>
                  </MenuList>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}

export default AccountDropdown;
